import React from 'react';
import usePaginatedResource from '../../../hooks/usePaginatedResource';
import PageHeader from '../../../components/shared/PageHeader/PageHeader';
import DataTable, { Column } from '../../../components/shared/DataTable/DataTable';
import type { Transaction, TransactionType } from '../../../types/api';

const TYPE_LABEL: Record<TransactionType, string> = {
  deposit: 'Deposit',
  withdrawal: 'Withdrawal',
  payment: 'Payment',
  refund: 'Refund',
  office_deposit: 'Office deposit',
};

const TX_TONE: Record<string, { bg: string; text: string; dot: string }> = {
  completed: { bg: 'bg-emerald-100', text: 'text-emerald-800', dot: 'bg-emerald-500' },
  failed:    { bg: 'bg-rose-100',    text: 'text-rose-800',    dot: 'bg-rose-500' },
  pending:   { bg: 'bg-amber-100',   text: 'text-amber-800',   dot: 'bg-amber-500' },
};

const Transactions: React.FC = () => {
  const { data: txs, loading } = usePaginatedResource<Transaction>('/finance/transactions/');

  const columns: Column<Transaction>[] = [
    { key: 'reference', header: 'Reference', sortable: true,
      render: (t) => <span className="font-mono text-sm">{t.reference}</span> },
    { key: 'transaction_type', header: 'Type', filterable: true,
      filterOptions: Object.entries(TYPE_LABEL).map(([value, label]) => ({ value, label })),
      render: (t) => TYPE_LABEL[t.transaction_type] ?? t.transaction_type },
    { key: 'amount', header: 'Amount', align: 'right',
      render: (t) => `KES ${Number(t.amount).toLocaleString()}` },
    { key: 'status', header: 'Status',
      render: (t) => {
        const tone = TX_TONE[t.status] ?? TX_TONE.pending;
        return (
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${tone.bg} ${tone.text}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} aria-hidden />
            {t.status}
          </span>
        );
      } },
    { key: 'description', header: 'Description' },
    { key: 'created_at', header: 'Date', sortable: true,
      render: (t) => new Date(t.created_at).toLocaleString() },
  ];

  return (
    <>
      <PageHeader title="Transactions" description="All wallet transactions across the platform." />
      <DataTable<Transaction>
        columns={columns} data={txs} rowKey="id"
        loading={loading}
        emptyTitle="No transactions yet"
        emptyDescription="Transactions appear here as money moves through wallets."
      />
    </>
  );
};

export default Transactions;
